import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Clapperboard,
  Plus,
  ChevronDown,
  Bot,
  MessageSquare,
  Wrench,
  Type,
  Image as ImageIcon,
  GalleryHorizontalEnd,
  Crown,
  Sparkles,
  Check,
  X,
  Loader2,
  Mic,
  Palette,
  LayoutTemplate,
  Play,
  Film,
  Music,
  FileText,
  SlidersHorizontal,
} from 'lucide-react';
import { cn, assetFileError, assetKind, SUPPORTED_COMPOSER_TYPES } from '@/v2/utils';
import { checkPrompt, type BlockReason } from '@/v2/moderation/promptBlocklist';
import { PromptBlockedNotice } from '@/v2/components/PromptBlockedNotice';
import { Toggle } from '@/v2/components/Toggle';
import {
  MODEL_CONFIGS,
  FREE_MODEL_ID,
  getModelById,
  getSelectableModelById,
  modelForOperation,
  getModelsForOperation,
  chargedCreditsForOperation,
  type OperationType,
  type ModelConfig,
} from '@/v2/data/models';
// Real backend plumbing (V1 modules are imported read-only; V1 unchanged)
import { apiService } from '@/api/apiService';
import { v2Service } from '@/api/v2Service';
import { isProviderRouted } from '@/api/providerEndpoints';
import { FREE_MAX_DURATION_SEC, autoDurationSteps, capDuration } from '@/v2/freeCap';
import type { V2CreateAvatarResult, V2CloneVoiceResult, V2Voice } from '@/api/v2Types';
import { useUserStore } from '@/store/userStore';
import { AssetPickerModal, type PickedAsset, type PickKind } from '@/v2/components/AssetPickerModal';
import { FileAssetPickerModal } from '@/v2/components/FileAssetPickerModal';
import { AiAvatarModal, PhotoAvatarModal, DigitalTwinModal } from '@/v2/components/AvatarCreateModals';
import { CloneVoiceModal, DesignVoiceModal } from '@/v2/components/VoiceCreateModals';
import { BrandKitModal } from '@/v2/components/BrandKitModal';
import { PreviewPlayer } from '@/v2/components/PreviewPlayer';
import { consumeHomeSeed, readAppliedTemplate, writeAppliedTemplate, readAppliedCast, writeAppliedCast, consumeHomeManualSeed, peekHomeManualSeed, rememberOrderPrompt, type AppliedTemplate, type AppliedPick } from '@/v2/seed';
import {
  getModelById as getV1Model,
  calculateCredits,
} from '@/utils/modelConfigs';
import { CREDIT_COSTS, CREDIT_COST } from '@/api/config';
import { calculateHeygenCredits } from '@/utils/constants';
import { useUpgradeModal } from '@/v2/upgradeModal';
import { toast } from '@/v2/toast';
import { trackGenerationStarted, trackFirstGeneration } from '@/services/analyticsService';
import { trackAmpModelSelected, trackAmpOperationSelected, trackAmpAssetSelected, trackAmpProjectCreated, trackAmpAssetUploaded, assetSizeBucket } from '@/services/amplitudeService';

type HomeMode = 'agent' | 'manual';

const OPERATIONS: OperationType[] = [
  'text_to_video',
  'image_to_video',
  'first_last_frame',
  'multiple_images',
];

const operationIcon: Record<OperationType, typeof Type> = {
  text_to_video: Type,
  image_to_video: ImageIcon,
  first_last_frame: Clapperboard,
  multiple_images: GalleryHorizontalEnd,
};

const IMAGE_COUNT: Record<OperationType, { min: number; max: number }> = {
  text_to_video: { min: 0, max: 0 },
  image_to_video: { min: 1, max: 1 },
  first_last_frame: { min: 2, max: 2 },
  multiple_images: { min: 2, max: 3 },
};

function ModeSwitcher({
  mode,
  onChange,
}: {
  mode: HomeMode;
  onChange: (mode: HomeMode) => void;
}) {
  const { t } = useTranslation('v2');
  return (
    <div className="flex items-center justify-center mb-6 sm:mb-8">
      <div className="relative flex w-full max-w-xs sm:w-auto sm:max-w-none sm:inline-flex items-center bg-surface rounded-xl p-1 border border-glass-border">
        <div
          className={cn(
            'absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg bg-primary/15 border border-primary/30 transition-all duration-300 ease-out',
            mode === 'agent' ? 'left-1' : 'left-[calc(50%+2px)]'
          )}
        />
        <button
          onClick={() => onChange('agent')}
          className={cn(
            'relative flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 sm:min-w-[140px]',
            mode === 'agent' ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
          )}
        >
          <MessageSquare className={cn('w-4 h-4', mode === 'agent' && 'text-primary')} />
          {t('home.videoAgent')}
        </button>
        <button
          onClick={() => onChange('manual')}
          className={cn(
            'relative flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 sm:min-w-[140px]',
            mode === 'manual' ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
          )}
        >
          <Wrench className={cn('w-4 h-4', mode === 'manual' && 'text-primary')} />
          {t('home.manualCreation', { ns: 'translation' })}
        </button>
      </div>
    </div>
  );
}

// Vidu movement_amplitude → friendly labels (values verified against fal schema).
// labelKey is resolved with t() at render time so it follows the active language.
const MOTION_OPTIONS = [
  { value: 'auto', labelKey: 'home.motionAuto' },
  { value: 'small', labelKey: 'home.motionSubtle' },
  { value: 'medium', labelKey: 'home.motionMedium' },
  { value: 'large', labelKey: 'home.motionDynamic' },
];
// Kling cfg_scale (0–1) surfaced as three friendly presets.
const CFG_MAP: Record<'Loose' | 'Balanced' | 'Strict', number> = { Loose: 0.3, Balanced: 0.5, Strict: 0.8 };

// Free→paid manual-mode conversion: when a free user hits the paywall in manual
// mode we stash the ENTIRE form here (manual creates no order, so there's nothing
// else to resume). localStorage survives the Stripe redirect; on return as Pro we
// restore the form and auto-start the real render — mirroring the agentic flow.
const MANUAL_STASH_KEY = 'vivideo_manual_autogen';
type ManualIntent = {
  modelId: string; operation: OperationType; prompt: string;
  duration: string; aspectRatio: string; resolution: string;
  generateAudio: boolean; autoFix: boolean; negativePrompt: string; seed: string;
  style: string; motion: string; cfgPreset: 'Loose' | 'Balanced' | 'Strict';
  imageUrls: string[]; ts: number;
};

function PillSelector({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((option) => {
        const isSelected = value === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={cn(
              'px-5 py-2.5 rounded-full border transition-all text-sm font-medium min-w-[70px]',
              isSelected
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-glass-border hover:border-primary/30 text-text-secondary hover:text-text-primary'
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

function AspectRatioSelector({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  const getSize = (ratio: string) => {
    switch (ratio) {
      case '9:16':
      case '2:3':
      case '3:4':
        return { width: 36, height: 56 };
      case '1:1':
        return { width: 48, height: 48 };
      case '16:9':
      case '3:2':
        return { width: 60, height: 36 };
      default:
        return { width: 52, height: 42 };
    }
  };

  return (
    <div className="flex flex-wrap gap-4">
      {options.map((option) => {
        const size = getSize(option);
        const isSelected = value === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className="flex flex-col items-center gap-2"
          >
            <div
              className={cn(
                'flex items-center justify-center rounded-lg border-2 transition-all',
                isSelected ? 'border-primary bg-primary/10' : 'border-glass-border hover:border-primary/30'
              )}
              style={{ width: size.width, height: size.height }}
            >
              <span
                className={cn(
                  'text-[10px] font-medium',
                  isSelected ? 'text-primary' : 'text-text-secondary'
                )}
              >
                {option}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface DropdownOption {
  value: string;
  label: string;
  description?: string;
  icon?: typeof Type;
  iconUrl?: string;
  badge?: boolean;
}

function Dropdown({
  options,
  value,
  onChange,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 rounded-xl border bg-surface-light text-left transition-colors',
          open ? 'border-primary' : 'border-glass-border hover:border-primary/30'
        )}
      >
        {selected?.icon && <selected.icon className="w-4 h-4 text-text-secondary flex-shrink-0" />}
        {selected?.iconUrl && (
          <img
            // key remounts the img per icon — otherwise one broken favicon
            // leaves visibility:hidden stuck on the node for later selections
            key={selected.iconUrl}
            src={selected.iconUrl}
            alt=""
            className="w-5 h-5 rounded flex-shrink-0"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
        )}
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary truncate">
              {selected?.label}
            </span>
            {selected?.badge && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
          </span>
          {selected?.description && (
            <span className="block text-xs text-text-tertiary truncate">{selected.description}</span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-tertiary flex-shrink-0 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-2 max-h-72 overflow-y-auto py-1.5 bg-surface border border-glass-border rounded-xl shadow-xl animate-fade-in">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  isSelected ? 'bg-primary/10' : 'hover:bg-surface-light'
                )}
              >
                {opt.icon && (
                  <opt.icon
                    className={cn(
                      'w-4 h-4 flex-shrink-0',
                      isSelected ? 'text-primary' : 'text-text-secondary'
                    )}
                  />
                )}
                {opt.iconUrl && (
                  <img
                    src={opt.iconUrl}
                    alt=""
                    className="w-5 h-5 rounded flex-shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                )}
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-sm font-medium truncate',
                        isSelected ? 'text-primary' : 'text-text-primary'
                      )}
                    >
                      {opt.label}
                    </span>
                    {opt.badge && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                  </span>
                  {opt.description && (
                    <span className="block text-xs text-text-tertiary truncate">
                      {opt.description}
                    </span>
                  )}
                </span>
                {isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Uploaded assets carry an assetId (HeyGen); text documents (txt/md) instead carry
// their content — read in the browser, handed to the planner only, never uploaded.
type Attachment = { assetId?: string; docText?: string; name: string; previewUrl?: string; fileType: string };
// Owner-set cap (2026-07-08): at most 10 attachments per chat. HeyGen documents no
// files[] count limit, so this is purely a product/UX ceiling.
const MAX_ATTACHMENTS = 10;
type CreateAction = 'avatar-ai' | 'avatar-photo' | 'avatar-twin' | 'voice-clone' | 'voice-design' | 'brand-new';

// Avatar / voice / brand. Desktop keeps the ORIGINAL horizontal "cast" cards
// (icon left, label + selection right). Mobile is a compact 3-up row of VERTICAL
// tiles — icon on top, text below — with tighter padding so the icon + fonts read
// bigger on a phone.
function CastSelector({
  icon: Icon, kind, selection, onOpen, onClear,
}: {
  icon: typeof Bot; kind: PickKind; selection: PickedAsset | null;
  onOpen: () => void; onClear: () => void;
}) {
  const { t } = useTranslation('v2');
  const labels: Record<PickKind, string> = { avatar: t('home.avatar'), voice: t('home.voice'), brand: t('home.brand') };
  const has = !!selection;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        // MOBILE: vertical tile, tight padding, centered.
        'group relative flex flex-col items-center text-center gap-1.5 p-2 rounded-2xl border transition-all',
        // DESKTOP (sm+): the ORIGINAL horizontal card — unchanged.
        'sm:flex-row sm:items-center sm:text-left sm:gap-3 sm:p-2.5 sm:flex-1 sm:min-w-[150px]',
        has ? 'border-primary/40 bg-primary/5' : 'border-glass-border hover:border-primary/30 bg-surface-light/40'
      )}
    >
      <span className={cn(
        'relative shrink-0 rounded-xl overflow-hidden flex items-center justify-center',
        // bigger square icon on mobile; ORIGINAL sizes on desktop (avatar/brand portrait).
        'w-12 h-12', kind === 'voice' ? 'sm:w-11 sm:h-11' : 'sm:w-11 sm:h-14',
        has ? '' : 'bg-surface-light'
      )}>
        {selection?.imageUrl
          ? <img src={selection.imageUrl} alt="" className="w-full h-full object-cover" />
          : <Icon className={cn('w-6 h-6 sm:w-5 sm:h-5', has ? 'text-primary' : 'text-text-tertiary')} />}
      </span>
      <span className="min-w-0 w-full sm:w-auto sm:flex-1">
        <span className="block text-[10px] uppercase tracking-wide text-text-tertiary font-semibold">{labels[kind]}</span>
        <span className={cn('block text-sm font-semibold truncate', has ? 'text-text-primary' : 'text-text-secondary')}>
          {/* "Choose" is desktop-only — on mobile an unselected tile shows just the icon + label. */}
          {selection?.label || <span className="hidden sm:inline">{t('home.choose')}</span>}
        </span>
        {!has && <span className="hidden sm:block text-[10px] text-text-tertiary">{t('home.auto')}</span>}
      </span>
      {has ? (
        <span
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          className={cn(
            // mobile: small corner badge; desktop: the ORIGINAL inline control at the row's end.
            'absolute top-1 right-1 w-5 h-5 rounded-full bg-surface-light flex items-center justify-center text-text-tertiary hover:text-text-primary z-10',
            'sm:static sm:top-auto sm:right-auto sm:w-6 sm:h-6 sm:bg-transparent sm:hover:bg-surface-light sm:shrink-0'
          )}
        >
          <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      ) : (
        <Plus className="hidden sm:block shrink-0 w-4 h-4 text-text-tertiary group-hover:text-primary" />
      )}
    </button>
  );
}

// ── Auto-generate (one-shot HeyGen) — submit dropdown + V1-style duration ──────
type SubmitMode = 'agent' | 'auto';
const AUTO_STASH_KEY = 'vivideo_auto_autogen';
type AutoIntent = { prompt: string; avatarId: string | null; voiceId: string | null; brandRef: string | null; styleId: string | null; fileAssetIds: string[] | null; durationSec: number; ts: number };
// Non-linear duration steps (mirrors V1): finer at the short end, coarser later.
const AUTO_DURATION_STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180];
const AUTO_DEFAULT_DURATION = 30;

/** Shown after the user hits Auto-generate (like V1): pick the length on a slider,
 *  see the live credit cost, confirm → render. Drives both the credit charge and
 *  the duration sent to HeyGen. */
export function AutoDurationModal({ onClose, onConfirm, hideCredits, capped }: { onClose: () => void; onConfirm: (durationSec: number) => void; hideCredits?: boolean; capped?: boolean }) {
  const { t } = useTranslation('v2');
  // Free users: only the free length is offered.
  const steps = autoDurationSteps(AUTO_DURATION_STEPS, !!capped);
  const [idx, setIdx] = useState(() => Math.max(0, steps.indexOf(AUTO_DEFAULT_DURATION)));
  const duration = steps[idx];
  const credits = calculateHeygenCredits(duration);
  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text-primary">{t('home.durationTitle')}</h3>
        <p className="text-xs text-text-tertiary mt-1">{hideCredits ? t('home.durationSubtitleFree') : t('home.durationSubtitlePaid')}</p>

        <div className="text-center my-6">
          <span className="text-5xl font-extrabold gradient-text tabular-nums">{duration}s</span>
        </div>

        {capped ? (
          <p data-testid="free-length-note" className="text-xs text-text-tertiary text-center">
            {t('v2PlanCard.freeLengthLimit', { seconds: FREE_MAX_DURATION_SEC })}{' '}
            <button
              type="button"
              onClick={() => { onClose(); useUpgradeModal.getState().show({ kind: 'pro_feature' }); }}
              className="font-semibold text-primary hover:underline"
            >
              {t('v2PlanCard.upgradeForLonger')}
            </button>
          </p>
        ) : (<>
        <input
          type="range" min={0} max={steps.length - 1} step={1} value={idx}
          onChange={(e) => setIdx(Number(e.target.value))}
          className="w-full accent-[#8B5CF6] cursor-pointer"
        />
        <div className="flex justify-between text-[10px] text-text-tertiary mt-1 tabular-nums">
          <span>{steps[0]}s</span><span>{steps[steps.length - 1]}s</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
          {[15, 30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setIdx(steps.indexOf(d))}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors',
                duration === d ? 'bg-primary/20 border border-primary/40 text-primary' : 'bg-surface-light border border-glass-border text-text-secondary hover:text-text-primary'
              )}
            >
              {d}s
            </button>
          ))}
        </div>
        </>)}

        <button
          onClick={() => onConfirm(duration)}
          className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-bold hover:opacity-95 hover:shadow-glow transition-all"
        >
          <Sparkles className="w-5 h-5" /> {hideCredits ? t('home.generate') : t('home.generateWithCredits', { credits })}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function AgentMode() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const isPro = useUserStore((s) => s.isPro);
  // Routed users' agent orders are produced by a provider twin, not HeyGen: no
  // length step and no orientation step — the plan carries both, read off the brief.
  const videoProvider = useUserStore((s) => s.user?.videoProvider);
  const isRouted = isProviderRouted(videoProvider);
  const decrementCredits = useUserStore((s) => s.decrementCredits);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Composer moderation gate. Non-null => the notice is showing and NO
  // generation has been started (no HeyGen session, no fal submit, no order).
  const [blocked, setBlocked] = useState<BlockReason | null>(null);
  const [avatar, setAvatar] = useState<PickedAsset | null>(() => readAppliedCast().avatar as PickedAsset | null);
  const [voice, setVoice] = useState<PickedAsset | null>(() => readAppliedCast().voice as PickedAsset | null);
  const [brand, setBrand] = useState<PickedAsset | null>(() => readAppliedCast().brand as PickedAsset | null);
  // Avatar/voice/brand are power-user features that confused first-timers when
  // always visible — collapsed behind "Advanced options" by default, open when a
  // selection already exists (so applied cast is never silently hidden).
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(() => {
    const cast = readAppliedCast();
    return Boolean(cast.avatar || cast.voice || cast.brand);
  });
  // Submit mode (split button): 'agent' = plan in chat (default), 'auto' = one-shot.
  const [submitMode, setSubmitMode] = useState<SubmitMode>('agent');
  const [menuOpen, setMenuOpen] = useState(false);
  // Auto-generate: duration step + free-tier preview funnel.
  const [autoDurationOpen, setAutoDurationOpen] = useState(false);
  // Applied template (style) — persisted so it survives navigation; the user
  // applies it on the Templates page and can detach it here.
  const [template, setTemplate] = useState<AppliedTemplate | null>(() => readAppliedTemplate());
  const [templatePreview, setTemplatePreview] = useState(false);
  // Portrait templates get a portrait tile in the strip (detected from the
  // media itself — object-cover in a landscape frame crops 9:16 previews).
  const [tplPortrait, setTplPortrait] = useState(false);
  useEffect(() => { setTplPortrait(false); }, [template?.id]);
  const [pickerKind, setPickerKind] = useState<PickKind | null>(null);
  const [createModal, setCreateModal] = useState<CreateAction | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // Focus the composer on arrival so typing starts immediately — desktop only
  // (on touch devices autofocus pops the keyboard over the hero).
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) promptRef.current?.focus();
  }, []);
  // Auto-grow the prompt box as the user types longer lines (smooth height
  // animation), capped so it never takes over the page.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [prompt]);

  const setByKind = (a: PickedAsset) => {
    trackAmpAssetSelected({ assetType: a.kind, source: 'picker' });
    if (a.kind === 'avatar') setAvatar(a);
    else if (a.kind === 'voice') setVoice(a);
    else setBrand(a);
  };
  const selectionFor = (k: PickKind) => (k === 'avatar' ? avatar : k === 'voice' ? voice : brand);

  // ---- Auto-select what the user just created into the chat context ----
  // Brand kits + designed voices are ready instantly; avatars and cloned voices
  // train in the background, so we poll their status and select them the moment
  // they're usable. The selection then flows to the backend on the next message.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const onAvatarCreated = async (r: V2CreateAvatarResult) => {
    if (!userId) return;
    // NO avatar is usable at creation. HeyGen returns a look id immediately, but the look
    // isn't renderable until generation finishes (its `supported_api_engines` stays empty),
    // and real-person avatars (photo / digital_twin) also need async TRAINING + a browser
    // CONSENT approval. So we NEVER select on look_id alone — we poll until the avatar is
    // GENUINELY ready, then select it. (The bug: prompt/AI avatars were selected immediately
    // and went into videos before HeyGen had actually generated them.)
    const realPerson = r.kind === 'digital_twin' || r.kind === 'photo';
    if (!realPerson) {
      toast.info(t('home.avatarCreating', { name: r.display_name }));
    }
    // Poll until trained (≈ up to 5 min). For real-person avatars, auto-open the
    // HeyGen consent page the moment consent becomes available, then keep polling
    // until the subject approves AND training finishes — that consent step is
    // what was missing, so a digital twin could never actually become usable.
    let consentOpened = false;
    for (let i = 0; i < 50 && aliveRef.current; i++) {
      await sleep(6000);
      if (!aliveRef.current) return;
      try {
        const st = await v2Service.avatarStatus(userId, r.avatar_ref);
        const needsConsent = st.consent_status === 'pending' || st.status === 'pending_consent';
        if (needsConsent) {
          if (!consentOpened) {
            // Fetch HeyGen's consent URL and open it for the subject to approve.
            // If it isn't ready yet (still early in training) we just retry on a
            // later poll — createAvatarConsent returns no url until HeyGen is ready.
            try {
              const c = await v2Service.createAvatarConsent(userId, r.avatar_ref, window.location.href);
              if (c.consent_url) {
                consentOpened = true;
                window.open(c.consent_url, '_blank', 'noopener');
                toast.info(t('home.avatarApproveConsent', { name: r.display_name }));
              }
            } catch { /* not ready yet — retry on the next poll */ }
          }
          continue; // wait for approval + training; don't select an unready twin
        }
        const lookId = st.avatar_id || st.looks?.[0]?.avatar_id;
        if ((st.status === 'completed' || st.status === 'ready') && lookId) {
          setAvatar({ kind: 'avatar', id: lookId, label: r.display_name, imageUrl: st.preview_image_url || st.looks?.[0]?.preview_image_url });
          toast.success(t('home.avatarReady', { name: r.display_name }));
          return;
        }
      } catch { /* keep polling */ }
    }
    // Training/consent slower than our window — leave a breadcrumb so it isn't lost.
    if (aliveRef.current) {
      toast.info(t('home.avatarStillFinishing', { name: r.display_name }));
    }
  };

  const onVoiceCloned = async (r: V2CloneVoiceResult) => {
    if (!userId) return;
    if (r.status === 'ready' && r.voice_clone_id) {
      setVoice({ kind: 'voice', id: r.voice_clone_id, label: r.display_name });
      return;
    }
    for (let i = 0; i < 50 && aliveRef.current; i++) {
      await sleep(6000);
      if (!aliveRef.current) return;
      try {
        const st = await v2Service.voiceStatus(userId, r.voice_ref);
        if (st.status === 'ready' && st.voice_id) {
          setVoice({ kind: 'voice', id: st.voice_id, label: st.display_name || r.display_name });
          toast.success(t('home.voiceCloneReady', { name: st.display_name || r.display_name }));
          return;
        }
      } catch { /* keep polling */ }
    }
  };

  const onVoiceDesigned = (v: V2Voice) => {
    setVoice({ kind: 'voice', id: v.voice_id, label: v.name, previewAudioUrl: v.preview_audio_url });
  };

  const onCreate = (action: CreateAction) => {
    // Every creation flow happens right here in a modal — no redirect to /assets.
    setPickerKind(null);
    setCreateModal(action);
  };

  // Pick up an asset chosen on the Assets/Templates pages (one-shot seed).
  useEffect(() => {
    const seed = consumeHomeSeed();
    if (!seed) return;
    if (seed.avatarId) setAvatar({ kind: 'avatar', id: seed.avatarId, label: seed.avatarName || t('home.avatar'), imageUrl: seed.avatarImg });
    if (seed.voiceId) setVoice({ kind: 'voice', id: seed.voiceId, label: seed.voiceName || t('home.voice') });
    if (seed.brandRef) setBrand({ kind: 'brand', id: seed.brandRef, label: seed.brandName || t('home.brand'), imageUrl: seed.brandLogo });
    if (seed.avatarId) trackAmpAssetSelected({ assetType: 'avatar', source: 'assets_page' });
    if (seed.voiceId) trackAmpAssetSelected({ assetType: 'voice', source: 'assets_page' });
    if (seed.brandRef) trackAmpAssetSelected({ assetType: 'brand', source: 'assets_page' });
    if (seed.styleId) {
      const tpl: AppliedTemplate = { id: seed.styleId, name: seed.styleName || t('home.template'), img: seed.styleImg, video: seed.stylePreviewVideo };
      writeAppliedTemplate(tpl);
      setTemplate(tpl);
    }
    if (seed.fileAssetId) {
      // Library file → pre-attached ({asset_id} shape, same as direct uploads).
      setAttachments((prev) => prev.some((a) => a.assetId === seed.fileAssetId) ? prev : [...prev, {
        assetId: seed.fileAssetId!,
        name: seed.fileName || t('home.libraryFile'),
        fileType: seed.fileType || 'file',
        previewUrl: seed.filePreviewUrl,
      }]);
    }
  }, []);

  // Persist the cast (avatar/voice/brand) just like the template, so a pick survives leaving
  // the page — on remount the chips are restored from storage (the useState initializers above)
  // instead of resetting to empty. Clearing a chip (setX(null)) clears it from storage too.
  useEffect(() => {
    writeAppliedCast({
      avatar: avatar as AppliedPick | null,
      voice: voice as AppliedPick | null,
      brand: brand as AppliedPick | null,
    });
  }, [avatar, voice, brand]);

  // Attachments follow HeyGen's Assets API guide: v3 direct upload (no size
  // hop through Lambda; png/jpeg, mp4/webm, mp3/wav, pdf) → asset_id, sent to
  // the session as the discriminated {type:'asset_id'} shape. Files also land
  // in the user's library. Falls back to the prod-proven legacy uploader.
  const addFiles = async (files: FileList | null) => {
    if (!files?.length || !userId) return;
    setError(null);
    for (const file of Array.from(files).slice(0, MAX_ATTACHMENTS - attachments.length)) {
      const bad = assetFileError(file, { allowText: true, allowAudio: true });
      if (bad) {
        setError(bad.reason === 'unsupported'
          ? t('home.unsupportedType', { name: file.name, types: SUPPORTED_COMPOSER_TYPES })
          : t('home.fileTooLarge', { name: file.name, max: bad.maxMb }));
        continue;
      }
      // Text documents (txt/md): read in the browser and carried as planner context —
      // no upload, no HeyGen asset. Capped so a huge file can't blow up the request.
      if (assetKind(file) === 'text') {
        try {
          const text = (await file.text()).slice(0, 20000);
          if (text.trim()) {
            setAttachments((prev) => [...prev, { docText: text, name: file.name, fileType: 'text' }]);
          }
        } catch {
          setError(t('home.couldNotAttach', { name: file.name }));
        }
        continue;
      }
      try {
        setUploadPct(1);
        let assetId: string;
        let fileType = file.type;
        try {
          const asset = await v2Service.uploadAsset(userId, file, (p) => setUploadPct(p));
          assetId = asset.asset_id;
          fileType = asset.asset_type || file.type;
        } catch {
          const res = await apiService.uploadHeygenAsset(userId, file, (p) => setUploadPct(p));
          assetId = res.assetId;
          fileType = res.fileType || file.type;
        }
        trackAmpAssetUploaded({ source: 'home_composer', fileType, sizeBucket: assetSizeBucket(file.size) });
        setAttachments((prev) => [...prev, {
          assetId,
          name: file.name,
          fileType,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }]);
      } catch {
        setError(t('home.couldNotAttach', { name: file.name }));
      } finally {
        setUploadPct(null);
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = (modeOverride?: SubmitMode) => {
    const m = modeOverride ?? submitMode;
    // Block while a file is still uploading — attachments are only added to
    // `attachments` once the upload resolves, so submitting now would silently
    // drop the file. The send button is disabled too; this guards Enter.
    if (!userId || !prompt.trim() || busy || uploadPct !== null) return;
    // MODERATION GATE — before every branch. Nothing downstream may run: no
    // HeyGen session, no fal submit, no Orders row, no navigation.
    const gate = checkPrompt(prompt);
    if (gate.blocked) {
      setError(null);
      setBlocked(gate.reason);
      return;
    }
    setBlocked(null);
    if (m === 'auto') {
      // Text documents are planner context — Auto mode uses HeyGen's own planner,
      // which can't read them. Fail loudly instead of silently dropping the doc.
      if (attachments.some((a) => a.docText)) {
        setError(t('home.textDocsAgentOnly'));
        return;
      }
      // AUTO-GENERATE → ask for the length first (V1-style), then render. No chat.
      setError(null);
      setAutoDurationOpen(true);
      return;
    }
    startAgent();
  };

  // AGENT (default) — open the agentic chat at /projects/new (unchanged). The chat
  // view creates the project + shows the thinking animation while the planner works;
  // the cast/brand selection rides along in router state.
  // Owner taxonomy: the SUBMIT is the Generation Requested moment for agent mode.
  const startAgent = (durationSec?: number) => {
    trackGenerationStarted({ operationType: 'heygen_chat', mode: 'agentic', freeUser: !isPro() });
    setBusy(true);
    navigate('/projects/new', {
      state: {
        prompt: prompt.trim(),
        // Provider-routed orders: no orientation is sent, so the director picks the
        // format from the brief; `durationSec` stays available for a caller that has
        // one (the storyboard card is where the planned length is confirmed).
        ...(isRouted ? { durationSec } : {}),
        selection: {
          avatarId: avatar?.id ?? null,
          avatarName: avatar?.label ?? null,
          avatarImage: avatar?.imageUrl ?? null,
          voiceId: voice?.id ?? null,
          voiceName: voice?.label ?? null,
          voicePreviewUrl: voice?.previewAudioUrl ?? null,
          brandRef: brand?.id ?? null,
          brandName: brand?.label ?? null,
          brandLogo: brand?.imageUrl ?? null,
          // HeyGen style (applied template) + reference attachments → forwarded to the
          // render by the backend, just like the cast/brand above.
          styleId: template?.id ?? null,
          styleName: template?.name ?? null,
          fileAssetIds: attachments.some((a) => a.assetId)
            ? attachments.filter((a) => a.assetId).map((a) => a.assetId!)
            : null,
          // txt/md contents for the Claude planner (session-level, like attachments).
          docTexts: attachments.some((a) => a.docText)
            ? attachments.filter((a) => a.docText).map((a) => ({ name: a.name, text: a.docText! }))
            : null,
        },
      },
    });
  };

  // The actual auto-generate run, once the user has picked a length. Free users get
  // the same preview→paywall funnel as Manual (NO HeyGen call); Pro users render now.
  const submitAuto = async (requestedSec: number) => {
    if (!userId || !prompt.trim() || uploadPct !== null) return;
    // Free users: at most the free length (the server clamps too).
    const durationSec = capDuration(requestedSec, !isPro());
    // Re-checked here, not only in submit(): the auto-intent restore path calls
    // submitAuto directly after a subscribe round-trip, bypassing submit().
    const gate = checkPrompt(prompt);
    if (gate.blocked) {
      setAutoDurationOpen(false);
      setError(null);
      setBlocked(gate.reason);
      return;
    }
    setBlocked(null);
    setAutoDurationOpen(false);
    // Analytics (V1 parity): fire ONCE per submit, before the free/pro branch.
    // trackGenerationStarted self-fans-out to GA + Amplitude; trackFirstGeneration
    // is localStorage-guarded to fire only once ever. mode:'auto' is what separates
    // this from MANUAL text-to-video in the funnel — operationType alone can't.
    trackGenerationStarted({ operationType: 'text_to_video', duration: `${durationSec}s`, mode: 'autogenerate', freeUser: !isPro() });
    trackFirstGeneration('text_to_video');
    if (!isPro()) {
      // FREE — exactly ONE project across ALL modes. Claim it, then create a PERSISTENT
      // PREVIEW order (no charge, no HeyGen) and open it as a project; it renders for real
      // on subscribe (project-page auto-resume).
      setBusy(true); setError(null);
      // Fail CLOSED on a claim error: this is the ONLY gate for the 1-free-project cap
      // (preview-create doesn't re-check), so a transient error must show a retryable
      // error — not silently wave the user through the cap.
      let claim: { allowed: boolean; existingOrderId?: string; existingVideoProvider?: string | null };
      try { claim = await v2Service.claimFreeProject(userId); } catch {
        setError(t('home.couldNotStart')); setBusy(false);
        return;
      }
      setAutoDurationOpen(false);
      if (!claim.allowed) {
        setBusy(false);
        useUpgradeModal.getState().show({ kind: 'free_project_limit', existingOrderId: claim.existingOrderId, existingVideoProvider: claim.existingVideoProvider });
        return;
      }
      try {
        const r = await v2Service.createAutoOrder({
          userId, prompt: prompt.trim(), durationSec,
          avatarId: avatar?.id ?? null, voiceId: voice?.id ?? null, brandRef: brand?.id ?? null,
          styleId: template?.id ?? null,
          fileAssetIds: attachments.some((a) => a.assetId) ? attachments.filter((a) => a.assetId).map((a) => a.assetId!) : null,
          preview: true,
        });
        // No orderId → navigating to /projects/undefined would blank-page with no
        // funnel and no error (mirrors the manual path's guard).
        if (!r.orderId) { setError(t('home.couldNotStart')); setBusy(false); return; }
        trackAmpProjectCreated({ mode: 'auto', free: true });
        navigate(`/projects/${r.orderId}`);
      } catch {
        setError(t('home.couldNotStart')); setBusy(false);
      }
      return;
    }
    // PRO — real one-shot HeyGen render (mode='generate'), then to the project page.
    setBusy(true); setError(null);
    try {
      const r = await v2Service.createAutoOrder({
        userId, prompt: prompt.trim(), durationSec,
        avatarId: avatar?.id ?? null, voiceId: voice?.id ?? null, brandRef: brand?.id ?? null,
        styleId: template?.id ?? null,
        fileAssetIds: attachments.some((a) => a.assetId) ? attachments.filter((a) => a.assetId).map((a) => a.assetId!) : null,
      });
      trackAmpProjectCreated({ mode: 'auto', free: false });
      decrementCredits(r.creditsCharged);
      // Guard like the free path: navigating to /projects/undefined would blank-page.
      navigate(r.orderId ? `/projects/${r.orderId}` : '/projects');
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const st = (e as any)?.response?.status;
      if (st === 402) navigate('/subscription?context=insufficient_credits');
      else if (st === 403) { useUpgradeModal.getState().show({ kind: 'pro_feature' }); setBusy(false); }
      else { setError(t('home.couldNotStartGeneration')); setBusy(false); }
    }
  };

  // ── AUTO-RESUME (free → paid conversion) ── On return from checkout as Pro with a
  // stashed auto intent, fire the REAL one-shot render automatically (no second click).
  const autoResumeFired = useRef(false);
  useEffect(() => {
    if (autoResumeFired.current || !userId) return;
    let intent: AutoIntent | null = null;
    try { const raw = localStorage.getItem(AUTO_STASH_KEY); if (raw) intent = JSON.parse(raw); } catch { intent = null; }
    if (!intent) return;
    if (intent.ts && Date.now() - intent.ts > 24 * 60 * 60 * 1000) { // stale → drop
      try { localStorage.removeItem(AUTO_STASH_KEY); } catch { /* ignore */ }
      autoResumeFired.current = true;
      return;
    }
    if (!isPro()) return; // still free (mid-payment) → wait
    autoResumeFired.current = true;
    try { localStorage.removeItem(AUTO_STASH_KEY); } catch { /* ignore */ }
    void (async () => {
      try {
        const r = await v2Service.createAutoOrder({
          userId, prompt: intent!.prompt, durationSec: intent!.durationSec,
          avatarId: intent!.avatarId, voiceId: intent!.voiceId, brandRef: intent!.brandRef,
          styleId: intent!.styleId ?? null, fileAssetIds: intent!.fileAssetIds ?? null,
        });
        decrementCredits(r.creditsCharged);
        // Guard like the free path: navigating to /projects/undefined would blank-page.
        navigate(r.orderId ? `/projects/${r.orderId}` : '/projects');
      } catch { navigate('/projects'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isPro]);

  return (
    <>
    <div className="glass rounded-3xl p-5 border border-transparent focus-within:border-primary/30 focus-within:shadow-glow-sm transition-all duration-300">
      {/* Avatar / voice / brand — collapsed behind a small "Advanced options"
          toggle so a first-time user sees exactly one job: describe the video.
          Opens by default when a cast selection already exists (never silently
          hide an applied avatar/voice/brand). */}
      <button
        onClick={() => setAdvancedOpen((v) => !v)}
        className="flex items-center gap-1.5 mb-4 text-xs font-medium text-text-tertiary hover:text-text-secondary transition-colors"
        aria-expanded={advancedOpen}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
        {t('home.advancedOptions')}
        {!advancedOpen && [avatar, voice, brand].filter(Boolean).length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold">
            {[avatar, voice, brand].filter(Boolean).length}
          </span>
        )}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', advancedOpen && 'rotate-180')} />
      </button>
      {/* Smooth expand/collapse: CSS grid-rows 0fr→1fr animates height without JS
          measuring; content stays mounted so selections never reset. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          advancedOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden min-h-0">
          <div
            className={cn(
              'grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-2.5 mb-5 transition-opacity duration-300',
              advancedOpen ? 'opacity-100' : 'opacity-0',
            )}
          >
            <CastSelector icon={Bot} kind="avatar" selection={avatar} onOpen={() => setPickerKind('avatar')} onClear={() => setAvatar(null)} />
            <CastSelector icon={Mic} kind="voice" selection={voice} onOpen={() => setPickerKind('voice')} onClear={() => setVoice(null)} />
            <CastSelector icon={Palette} kind="brand" selection={brand} onOpen={() => setPickerKind('brand')} onClear={() => setBrand(null)} />
            {/* No orientation control: the director reads the format off the brief. */}
          </div>
        </div>
      </div>

      {pickerKind && (
        <AssetPickerModal
          kind={pickerKind}
          selectedId={selectionFor(pickerKind)?.id}
          onClose={() => setPickerKind(null)}
          onSelect={(a) => { setByKind(a); setPickerKind(null); }}
          onCreate={onCreate}
        />
      )}
      {filePickerOpen && userId && (
        <FileAssetPickerModal
          userId={userId}
          allowAudio
          attachedIds={attachments.filter((a) => a.assetId).map((a) => a.assetId!)}
          onClose={() => setFilePickerOpen(false)}
          onAttach={(items) => setAttachments((prev) => {
            const have = new Set(prev.map((a) => a.assetId));
            const adds: Attachment[] = items
              .filter((it) => !have.has(it.assetId))
              .map((it) => ({
                assetId: it.assetId,
                name: it.name,
                fileType: it.type || 'file',
                previewUrl: it.type === 'image' && it.url ? it.url : undefined,
              }));
            return [...prev, ...adds].slice(0, MAX_ATTACHMENTS);
          })}
        />
      )}
      {createModal === 'avatar-ai' && <AiAvatarModal onClose={() => setCreateModal(null)} onCreated={(r) => void onAvatarCreated(r)} />}
      {createModal === 'avatar-photo' && <PhotoAvatarModal onClose={() => setCreateModal(null)} onCreated={(r) => void onAvatarCreated(r)} />}
      {createModal === 'avatar-twin' && <DigitalTwinModal onClose={() => setCreateModal(null)} onCreated={(r) => void onAvatarCreated(r)} />}
      {createModal === 'voice-clone' && <CloneVoiceModal onClose={() => setCreateModal(null)} onCreated={(r) => void onVoiceCloned(r)} />}
      {createModal === 'voice-design' && <DesignVoiceModal onClose={() => setCreateModal(null)} onCreated={onVoiceDesigned} />}
      {createModal === 'brand-new' && (
        <BrandKitModal
          onClose={() => setCreateModal(null)}
          onSaved={(b) => setBrand({ kind: 'brand', id: b.brand_ref, label: b.name, imageUrl: b.logo_url })}
        />
      )}

      {/* Auto-generate: pick the length (drives credits + the HeyGen duration). */}
      {autoDurationOpen && (
        <AutoDurationModal onClose={() => setAutoDurationOpen(false)} onConfirm={(d) => void submitAuto(d)} hideCredits={!isPro()} capped={!isPro()} />
      )}
      {/* Free-tier Auto-generate funnel now lives on its OWN project-page-style route
          (/auto-preview → AutoPreviewPage); submitAuto navigates there for free users,
          so it reads as a real project instead of a Home pop-up. */}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachments.map((a, i) => {
            const FileIcon = a.fileType === 'video' ? Film : a.fileType === 'audio' ? Music : (a.fileType === 'document' || a.fileType === 'text') ? FileText : ImageIcon;
            return (
            <span key={a.assetId} className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full bg-surface-light border border-glass-border text-xs text-text-primary animate-fade-in">
              {a.previewUrl
                ? <img src={a.previewUrl} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                : <span className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0"><FileIcon className="w-3 h-3 text-primary" /></span>}
              <span className="max-w-[140px] truncate">{a.name}</span>
              <button onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-text-tertiary hover:text-text-primary">
                <X className="w-3 h-3" />
              </button>
            </span>
            );
          })}
        </div>
      )}
      <textarea
        ref={promptRef}
        rows={1}
        value={prompt}
        // Clear the refusal as soon as the user starts editing — leaving it up
        // while they fix the prompt reads as though the edit did nothing.
        onChange={(e) => { setPrompt(e.target.value); if (blocked) setBlocked(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }}
        placeholder={t('home.agentPromptPlaceholder')}
        // min-h keeps the empty box ~3 lines tall (bigger canvas invites longer
        // prompts); it still auto-grows with content up to the 320px cap.
        className="w-full bg-transparent text-text-primary placeholder:text-text-tertiary text-lg outline-none mb-6 resize-none overflow-hidden leading-relaxed transition-[height] duration-150 ease-out min-h-[88px]"
        disabled={busy}
      />

      <PromptBlockedNotice reason={blocked} onClose={() => setBlocked(null)} />
      {error && <p className="text-xs text-amber-300/80 mb-3">{error}</p>}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept="image/*,video/*,audio/*,application/pdf,text/plain,.txt,.md,.markdown" className="hidden" onChange={(e) => void addFiles(e.target.files)} />
          <button
            onClick={() => (userId ? setFilePickerOpen(true) : fileRef.current?.click())}
            disabled={uploadPct !== null}
            className="flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full bg-surface-light text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-60"
            title={t('home.attachFileTitle')}
          >
            {uploadPct !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {uploadPct !== null ? `${Math.round(uploadPct)}%` : t('home.addFile')}
          </button>
          {/* Length, orientation and captions are now decided by the agent from
              the user's own words (e.g. "make a 30s vertical video, no captions"). */}
        </div>
        {/* Split submit: the main button runs the chosen mode; the chevron switches
            between Agent (chat) and Auto-generate (one-shot). The composer is identical
            for both — only what happens on submit differs. */}
        <div className="relative flex items-center">
          {menuOpen && <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />}
          {menuOpen && (
            <div className="absolute bottom-full right-0 mb-2 w-72 rounded-2xl bg-[#1b1b24] border border-white/10 shadow-2xl py-1.5 z-30 animate-fade-in">
              {([
                ['agent', t('home.menuAgentLabel'), t('home.menuAgentDesc'), MessageSquare],
                ['auto', t('home.menuAutoLabel'), t('home.menuAutoDesc'), Sparkles],
              ] as const).map(([m, label, desc, Icon]) => (
                <button
                  key={m}
                  onClick={() => { setSubmitMode(m); setMenuOpen(false); void submit(m); }}
                  className="w-full flex items-start gap-3 px-3.5 py-2.5 text-left hover:bg-white/5 transition-colors"
                >
                  <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', submitMode === m ? 'text-primary' : 'text-text-tertiary')} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-text-primary">
                      {label}{submitMode === m && <Check className="w-3.5 h-3.5 text-primary" />}
                    </span>
                    <span className="block text-[11px] text-text-tertiary leading-snug mt-0.5">{desc}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className={cn(
            'flex items-stretch rounded-full overflow-hidden bg-gradient-to-r from-primary to-accent transition-shadow duration-300',
            // Glow only once there's something to submit — the cue that distinguishes
            // the (otherwise identical) empty vs filled states. (Clearly visible, not the
            // faint shadow-glow-sm.)
            prompt.trim() ? 'shadow-[0_0_24px_rgba(139,92,246,0.7)]' : 'shadow-none'
          )}>
            {/* LEFT: mode toggle — the current mode's icon (Agent = robot, Auto = stars)
                + a caret. Opens the menu to switch. */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              disabled={busy}
              className="pl-4 pr-2 py-2 text-white border-r border-white/25 hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center gap-1"
              aria-label={t('home.chooseHowToGenerate')}
              title={submitMode === 'auto' ? t('home.switchModeAuto') : t('home.switchModeAgent')}
            >
              {/* Agent sub-mode = "plan it in chat" → a chat bubble (NOT the robot, which is the avatar selector's icon). */}
              {submitMode === 'auto' ? <Sparkles className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
              <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', menuOpen && 'rotate-180')} />
            </button>
            {/* RIGHT: the main submit, runs the current mode. */}
            <button
              onClick={() => void submit()}
              disabled={busy || !prompt.trim() || uploadPct !== null}
              // Text stays WHITE when empty (no opacity fade) — still uninteractable
              // (disabled + not-allowed cursor); the glow above is the only state cue.
              className="pl-5 pr-7 py-2 text-white text-sm font-semibold hover:opacity-95 transition-all disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? t('home.starting') : submitMode === 'auto' ? t('home.autoGenerate') : t('home.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Template strip — lives just below the chat box. Templates are real
        HeyGen styles applied via the backend (style_id); brand kits above are
        the reverse-engineered injection. Hidden for provider-routed users
        (a HeyGen style has nothing to apply to). */}
    {!isRouted && <div className="mt-3">
      {template ? (
        <div className="flex items-center gap-4 p-3 rounded-2xl border border-primary/25 bg-surface-light/40 animate-fade-in">
          {/* Live-running preview of the applied template */}
          <button
            onClick={() => setTemplatePreview(true)}
            className={cn(
              'relative rounded-xl overflow-hidden bg-black shrink-0 group ring-1 ring-white/10',
              tplPortrait ? 'w-[64px] h-[114px]' : 'w-44 h-[100px]'
            )}
            title={t('home.openTemplatePreview')}
          >
            {template.video ? (
              <video
                src={template.video}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                onLoadedMetadata={(e) => setTplPortrait(e.currentTarget.videoHeight > e.currentTarget.videoWidth)}
              />
            ) : template.img ? (
              <img
                src={template.img}
                alt=""
                className="w-full h-full object-cover"
                onLoad={(e) => setTplPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)}
              />
            ) : (
              <LayoutTemplate className="w-5 h-5 text-text-tertiary m-auto" />
            )}
            <span className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="w-4 h-4 text-black fill-black ml-0.5" />
              </span>
            </span>
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-primary font-bold">{t('home.templateApplied')}</p>
            <p className="text-base font-bold text-text-primary truncate mt-0.5">{template.name}</p>
            <p className="text-xs text-text-tertiary mt-1">{t('home.templateAppliedDesc')}</p>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <button
              onClick={() => { writeAppliedTemplate(null); setTemplate(null); }}
              title={t('home.removeTemplate')}
              className="w-7 h-7 rounded-full hover:bg-surface-light flex items-center justify-center text-text-tertiary hover:text-text-primary"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/templates')}
              className="px-3.5 py-1.5 rounded-full glass text-xs font-semibold text-text-secondary hover:text-text-primary transition-colors"
            >
              {t('home.change')}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => navigate('/templates')}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border border-dashed border-glass-border text-sm text-text-tertiary hover:text-primary hover:border-primary/40 transition-colors"
        >
          <LayoutTemplate className="w-4 h-4" /> {t('home.tryTemplates')}
        </button>
      )}
    </div>}

    {/* Template preview — same in-house player as the Templates gallery
        (sound on, autoplay, play/pause + time bar; no native controls) */}
    {templatePreview && template && createPortal(
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={() => setTemplatePreview(false)}>
        <div
          className={cn(
            'w-full rounded-2xl overflow-hidden border border-glass-border bg-surface animate-slide-up',
            tplPortrait ? 'max-w-md' : 'max-w-2xl'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={cn('relative bg-black', tplPortrait ? 'aspect-[9/16] max-h-[64vh] mx-auto' : 'aspect-video')}>
            <PreviewPlayer src={template.video} poster={template.img} portrait={tplPortrait} autoPlay />
          </div>
          <div className="flex items-center gap-3 p-4">
            <p className="flex-1 text-sm font-semibold text-text-primary truncate">{template.name}</p>
            <button
              onClick={() => { writeAppliedTemplate(null); setTemplate(null); setTemplatePreview(false); }}
              className="px-4 py-2 rounded-full border border-glass-border text-xs font-semibold text-text-secondary hover:text-red-400 hover:border-red-400/40 transition-colors"
            >
              {t('home.removeTemplate')}
            </button>
            <button onClick={() => setTemplatePreview(false)} className="px-4 py-2 rounded-full bg-primary text-white text-xs font-semibold">
              {t('home.done')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
  </>
  );
}

// ---------------------------------------------------------------------------
// Manual mode — real createOrder (same payload as V1)
// ---------------------------------------------------------------------------

function ManualMode() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const hasEnoughCredits = useUserStore((s) => s.hasEnoughCredits);
  const decrementCredits = useUserStore((s) => s.decrementCredits);
  const isPro = useUserStore((s) => s.isPro);
  const [operation, setOperation] = useState<OperationType>('text_to_video');
  const [modelId, setModelId] = useState<string>(FREE_MODEL_ID);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState('4s');
  const [resolution, setResolution] = useState('720p');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [autoFix, setAutoFix] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [seed, setSeed] = useState('');
  // Per-model advanced options (verified against the fal schema). style: fal
  // `style` enum ('' = model default). motion: Vidu `movement_amplitude`.
  // cfgPreset: friendly mapping over Kling `cfg_scale` (see CFG_MAP).
  const [style, setStyle] = useState('');
  const [motion, setMotion] = useState('auto');
  const [cfgPreset, setCfgPreset] = useState<'Loose' | 'Balanced' | 'Strict'>('Balanced');
  // Kling o3/v3 `shot_type`: 'customize' = one continuous shot (fal default),
  // 'intelligent' = Kling cuts the clip into multiple shots itself.
  const [shotType, setShotType] = useState<'customize' | 'intelligent'>('customize');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Composer moderation gate — see the agent composer's copy of this above.
  const [blocked, setBlocked] = useState<BlockReason | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const model = getModelById(modelId) || MODEL_CONFIGS[0];
  // V1's config carries the fal model mapping + credit math; ids match 1:1.
  const v1Model = getV1Model(modelId);

  const effDuration = model.durations.includes(duration) ? duration : model.defaultDuration;
  const effAspect = model.aspectRatios.includes(aspectRatio) ? aspectRatio : model.defaultAspectRatio;
  const effResolution = model.resolutions.includes(resolution) ? resolution : model.defaultResolution;
  // Credit pricing: V2-only models carry their own per-second rates; V1
  // models use the shared schema (calculateCredits + CREDIT_COSTS overrides).
  const creditCost = (() => {
    // Reference-to-video (Multiple Images) bills at the ENDPOINT's rate —
    // show the true server charge (Veo: 32, 48 at 4k; Vidu Q1: 5). The old
    // flat CREDIT_COSTS.multiple_images (25) under-quoted the real charge.
    const charged = chargedCreditsForOperation(model, operation, effResolution);
    if (charged !== null) return charged;
    if (operation === 'multiple_images') return CREDIT_COSTS.multiple_images ?? CREDIT_COST;
    if (model.creditsPerSecond) {
      const secs = parseInt(effDuration.replace('s', ''), 10) || 5;
      const rate = model.creditsPerSecond[effResolution] ?? Object.values(model.creditsPerSecond)[0] ?? 2;
      return secs * rate;
    }
    return v1Model ? calculateCredits(v1Model, effDuration, effResolution) : CREDIT_COST;
  })();

  const imgReq = IMAGE_COUNT[operation];
  const isValid = prompt.trim().length > 0 && imageUrls.length >= imgReq.min;

  // PixVerse caps 1080p at 5s ("1080p videos are limited to 5 seconds") —
  // snap the selection instead of letting fal reject the order.
  useEffect(() => {
    if (model.id === 'pixverse_v5' && effResolution === '1080p' && effDuration === '8s') {
      setDuration('5s');
    }
  }, [model.id, effResolution, effDuration]);

  const applyModelDefaults = (m: ModelConfig) => {
    if (!m.durations.includes(duration)) setDuration(m.defaultDuration);
    if (!m.aspectRatios.includes(aspectRatio)) setAspectRatio(m.defaultAspectRatio);
    if (!m.resolutions.includes(resolution)) setResolution(m.defaultResolution);
    // Advanced controls reset to each model's default on switch (so e.g. a
    // PixVerse 'clay' style never leaks onto a model that doesn't support it).
    setStyle(m.styleOptions?.[0]?.value ?? '');
    setMotion('auto');
    setCfgPreset('Balanced');
  };

  const handleOperationChange = (op: OperationType) => {
    setOperation(op);
    setImageUrls([]);
    // Amplitude-only (no GA twin) — user changed the manual operation.
    trackAmpOperationSelected(op);
    if (!model.operations.includes(op)) {
      const next = getModelsForOperation(op)[0];
      setModelId(next.id);
      applyModelDefaults(next);
    }
  };

  const handleModelChange = (m: ModelConfig) => {
    setModelId(m.id);
    applyModelDefaults(m);
    // Amplitude-only (no GA twin) — user picked a manual model.
    trackAmpModelSelected({ modelId: m.id, modelName: m.name, operation });
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || !userId) return;
    setUploading(true);
    setError(null);
    try {
      const remaining = imgReq.max - imageUrls.length;
      const list = Array.from(files).slice(0, Math.max(0, remaining));
      for (const file of list) {
        const url = await apiService.uploadImage(userId, file, file.type || 'image/jpeg');
        setImageUrls((prev) => [...prev, url]);
      }
    } catch {
      setError(t('home.imageUploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    // Block while an image is still uploading, or its URL wouldn't be in the payload yet.
    if (!userId || !isValid || busy || uploading) return;
    // MODERATION GATE — this is the exact path the banned Seedance 2.0
    // image-to-video order took. Nothing below runs when it trips: no credits
    // deducted, no fal submit, no preview order persisted.
    const gate = checkPrompt(prompt);
    if (gate.blocked) {
      setError(null);
      setBlocked(gate.reason);
      return;
    }
    setBlocked(null);
    const free = !isPro();

    // Build the exact manual payload ONCE — used by both the free preview and the paid render.
    const promptSnippet = prompt.trim().replace(/\s+/g, ' ').slice(0, 48);
    const titlePrompt = prompt.trim().length > 48 ? `${promptSnippet}…` : promptSnippet;
    const orderName = `${model.name} · ${titlePrompt}`.slice(0, 80);
    const orderData: Record<string, unknown> = {
      prompt: prompt.trim(),
      order_name: orderName,
      duration: operation === 'multiple_images' ? '8s' : effDuration,
      aspect_ratio: effAspect,
      resolution: effResolution,
      generate_audio: model.supportsAudio ? generateAudio : true,
    };
    const falModel = model.falModels?.[operation] ?? v1Model?.falModels[operation];
    if (falModel) orderData.fal_model = falModel;
    if (model.supportsNegativePrompt && negativePrompt.trim()) orderData.negative_prompt = negativePrompt.trim();
    if (model.supportsSeed && seed) orderData.seed = parseInt(seed, 10);
    if (model.supportsAutoFix) orderData.auto_fix = autoFix;
    if (model.styleOptions && style) orderData.style = style;
    if (model.supportsMotion) orderData.movement_amplitude = motion;
    if (model.supportsCfgScale) orderData.cfg_scale = CFG_MAP[cfgPreset];
    if (model.supportsShotType) orderData.shot_type = shotType;
    if (operation === 'image_to_video') {
      orderData.image_url = imageUrls[0];
    } else if (operation === 'first_last_frame') {
      orderData.first_frame_url = imageUrls[0];
      orderData.last_frame_url = imageUrls[1];
    } else if (operation === 'multiple_images') {
      orderData.image_urls = imageUrls;
    }
    const opType = operation === 'first_last_frame' ? 'first_frame_last_frame' : operation;

    // Analytics (V1 parity): fire ONCE per click, after validation, before the
    // free/pro branch (so both tiers count once). trackGenerationStarted
    // self-fans-out to GA + Amplitude; trackFirstGeneration is localStorage-guarded.
    trackGenerationStarted({
      operationType: opType,
      duration: effDuration,
      aspectRatio: effAspect,
      resolution: effResolution,
      hasImage: imageUrls.length > 0,
      imageCount: imageUrls.length,
      mode: 'manual',
      freeUser: free,
    });
    trackFirstGeneration(opType);

    // FREE TIER — exactly ONE project across ALL modes. Claim it, then ALWAYS create a PERSISTENT
    // PREVIEW order (no charge, no fal) and open it as a real project — identical to the auto and
    // agent paths. No model gate, no transient /auto-preview fallback (that old fallback created
    // NO order, so the project vanished the moment you left the page while the counter was already
    // burned). The free user still NEVER generates a real video here: the preview order has no fal
    // submit — the actual render only fires after they upgrade (project-page auto-resume).
    if (free) {
      setBusy(true); setError(null);
      // Fail CLOSED on a claim error (see submitAuto) — the cap has no server-side
      // re-check on preview-create, so an error here must not open the gate.
      let claim: { allowed: boolean; existingOrderId?: string; existingVideoProvider?: string | null };
      try { claim = await v2Service.claimFreeProject(userId); } catch {
        setError(t('home.couldNotStart')); setBusy(false);
        return;
      }
      if (!claim.allowed) {
        setBusy(false);
        useUpgradeModal.getState().show({ kind: 'free_project_limit', existingOrderId: claim.existingOrderId, existingVideoProvider: claim.existingVideoProvider });
        return;
      }
      try {
        const r = await v2Service.createManualOrder({ userId, falModel: falModel || '', operation: opType, orderData, preview: true });
        if (r.orderId) { rememberOrderPrompt(r.orderId, orderData.prompt as string); trackAmpProjectCreated({ mode: 'manual', free: true }); navigate(`/projects/${r.orderId}`); return; }
        setError(t('home.couldNotStart')); setBusy(false);
      } catch {
        setError(t('home.couldNotStart')); setBusy(false);
      }
      return;
    }

    // PAID TIER from here.
    if (!hasEnoughCredits(creditCost)) {
      navigate('/subscription?context=insufficient_credits');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // V2-only models: the gated v2-create-order knows each model's exact fal input schema
      // and charges credits server-side. The premium loader on the project page polls fal.
      if (model.v2Endpoint && falModel) {
        const r = await v2Service.createManualOrder({ userId, falModel, operation: opType, orderData });
        rememberOrderPrompt(r.orderId, orderData.prompt as string);
        trackAmpProjectCreated({ mode: 'manual', free: false });
        decrementCredits(r.creditsCharged || creditCost);
        navigate(r.orderId ? `/projects/${r.orderId}` : '/projects');
        return;
      }

      const res = await apiService.createOrder(userId, opType, orderData);
      rememberOrderPrompt(res.orderId, orderData.prompt as string);
      trackAmpProjectCreated({ mode: 'manual', free: false });
      decrementCredits(creditCost);
      navigate(res.orderId ? `/projects/${res.orderId}` : '/projects');
    } catch (err) {
      const msg = String(err).toLowerCase();
      if (msg.includes('402') || msg.includes('insufficient')) {
        navigate('/subscription?context=insufficient_credits');
        return;
      }
      if (msg.includes('403') || msg.includes('free_tier_locked')) {
        useUpgradeModal.getState().show({ kind: 'pro_feature' });
        return;
      }
      setError(t('home.couldNotCreateVideo'));
    } finally {
      setBusy(false);
    }
  };

  // ── TEMPLATES v2 PRESET SEED ──────────────────────────────────────────────
  // "Use this" on the Templates v2 gallery stashed a manual preset; apply it
  // ONCE on mount (model + prompt + duration + aspect/resolution), snapping any
  // value the chosen model doesn't support to that model's default. Image slots
  // are intentionally left empty — the user brings their own image.
  const manualSeedApplied = useRef(false);
  useEffect(() => {
    if (manualSeedApplied.current) return;
    manualSeedApplied.current = true;
    const s = consumeHomeManualSeed();
    if (!s) return;
    let op = (s.operation as OperationType) || 'text_to_video';
    const seedModel = (s.modelId && getSelectableModelById(s.modelId)) || getModelById(FREE_MODEL_ID) || MODEL_CONFIGS[0];
    const m = modelForOperation(seedModel, op);
    if (!m.operations.includes(op)) op = m.operations[0];
    setModelId(m.id);
    setOperation(op);
    setImageUrls([]);
    setDuration(s.duration && m.durations.includes(s.duration) ? s.duration : m.defaultDuration);
    setAspectRatio(s.aspectRatio && m.aspectRatios.includes(s.aspectRatio) ? s.aspectRatio : m.defaultAspectRatio);
    setResolution(s.resolution && m.resolutions.includes(s.resolution) ? s.resolution : m.defaultResolution);
    setStyle(m.styleOptions?.[0]?.value ?? '');
    setMotion('auto');
    setCfgPreset('Balanced');
    if (s.prompt) setPrompt(s.prompt);
    if (s.negativePrompt && m.supportsNegativePrompt) setNegativePrompt(s.negativePrompt);
  }, []);

  // ── AUTO-RESUME GENERATION (free → paid conversion, manual mode) ──────────
  // On return from checkout as a Pro user, restore the stashed form and then
  // auto-fire the REAL render — exactly as if they'd clicked Create themselves.
  const manualAutoFired = useRef(false);
  const [autoSubmit, setAutoSubmit] = useState(false);
  useEffect(() => {
    if (manualAutoFired.current || !userId) return;
    let intent: ManualIntent | null = null;
    try { const raw = localStorage.getItem(MANUAL_STASH_KEY); if (raw) intent = JSON.parse(raw); } catch { intent = null; }
    if (!intent) return;
    // Expired (>24h) → drop it; never auto-charge on a stale intent.
    if (intent.ts && Date.now() - intent.ts > 24 * 60 * 60 * 1000) {
      try { localStorage.removeItem(MANUAL_STASH_KEY); } catch { /* ignore */ }
      manualAutoFired.current = true;
      return;
    }
    if (!isPro()) return; // still free (mid-payment) → wait
    manualAutoFired.current = true;
    try { localStorage.removeItem(MANUAL_STASH_KEY); } catch { /* ignore */ }
    // Restore the exact form they had at the paywall. The stash can predate a
    // model losing an operation (Kling 2.1 Standard dropped text-to-video when
    // fal turned out to have no such endpoint), so reconcile before auto-firing
    // — otherwise falModels[op] is undefined and the order silently falls
    // through to the V1 default model. Same guard the seed path uses.
    const intentModel = modelForOperation(getSelectableModelById(intent.modelId) || MODEL_CONFIGS[0], intent.operation);
    const intentOp = intentModel.operations.includes(intent.operation)
      ? intent.operation
      : intentModel.operations[0];
    setOperation(intentOp); setModelId(intentModel.id);
    setPrompt(intent.prompt); setDuration(intent.duration);
    setAspectRatio(intent.aspectRatio); setResolution(intent.resolution);
    setGenerateAudio(intent.generateAudio); setAutoFix(intent.autoFix);
    setNegativePrompt(intent.negativePrompt); setSeed(intent.seed);
    setStyle(intent.style); setMotion(intent.motion); setCfgPreset(intent.cfgPreset);
    setImageUrls(intent.imageUrls || []);
    setAutoSubmit(true);
  }, [userId, isPro]);

  // Once the restored form has rendered, fire the paid generation exactly once.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!autoSubmit || autoSubmittedRef.current) return;
    if (!prompt.trim()) return; // wait for the restore to land
    autoSubmittedRef.current = true;
    setAutoSubmit(false);
    void handleCreate(); // now Pro → takes the paid path → navigates to the project
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, prompt, modelId]);

  const hasAdvanced =
    model.supportsAudio || model.supportsAutoFix || model.supportsNegativePrompt || model.supportsSeed ||
    !!model.styleOptions || model.supportsMotion || model.supportsCfgScale || model.supportsShotType;

  return (
    <div className="glass rounded-3xl p-6 space-y-6 text-left">
      {/* 1. Operation selection — icon tiles, one tap */}
      <div>
        <label className="block text-sm font-semibold text-text-primary mb-3">
          {t('home.whatToCreate')}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {OPERATIONS.map((op) => {
            const Icon = operationIcon[op];
            const active = operation === op;
            return (
              <button
                key={op}
                type="button"
                onClick={() => handleOperationChange(op)}
                className={cn(
                  'flex flex-col items-center gap-2 px-3 py-4 rounded-2xl border text-center transition-all duration-200',
                  active
                    ? 'border-primary/50 bg-primary/10 shadow-glow-sm'
                    : 'border-glass-border bg-surface-light/40 hover:border-primary/30 hover:-translate-y-0.5'
                )}
              >
                <span className={cn(
                  'w-9 h-9 rounded-xl flex items-center justify-center transition-colors',
                  active ? 'bg-gradient-to-br from-primary/30 to-accent/30' : 'bg-surface-light'
                )}>
                  <Icon className={cn('w-5 h-5', active ? 'text-primary' : 'text-text-secondary')} />
                </span>
                <span className={cn('text-xs font-semibold leading-tight', active ? 'text-text-primary' : 'text-text-secondary')}>
                  {t(`models.op.${op}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Model selection */}
      <div>
        <label className="block text-sm font-semibold text-text-primary mb-3">{t('home.selectModel')}</label>
        <Dropdown
          options={getModelsForOperation(operation).map((m) => ({
            value: m.id,
            label: m.name,
            // Fall back to the model's own tag when there's no localized key (was
            // showing the raw key "models.tag.seedance25"/"seedance2" for models
            // added without an i18n tag entry).
            description: t(`models.tag.${m.id}`, { defaultValue: m.tag }),
            iconUrl: m.brandIcon,
            badge: m.id !== FREE_MODEL_ID,
          }))}
          value={modelId}
          onChange={(v) => {
            const m = getModelById(v);
            if (m) handleModelChange(m);
          }}
        />
        <p className="text-xs text-text-tertiary mt-2">{t(`models.desc.${model.id}`, { defaultValue: model.description })}</p>
      </div>

      {/* 3. Prompt */}
      <div>
        <label className="block text-sm font-semibold text-text-primary mb-2">{t('home.prompt')}</label>
        <textarea
          rows={4}
          value={prompt}
          // Clear the refusal as soon as the user starts editing.
          onChange={(e) => { setPrompt(e.target.value); if (blocked) setBlocked(null); }}
          placeholder={t('home.promptPlaceholder')}
          className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary resize-none"
        />
      </div>

      {/* Image upload when operation needs images */}
      {operation !== 'text_to_video' && (
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {operation === 'multiple_images'
              ? t('home.referenceImages', { count: imageUrls.length, max: imgReq.max })
              : operation === 'first_last_frame'
                ? t('home.firstLastFrame', { count: imageUrls.length })
                : t('home.sourceImage')}
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={imgReq.max > 1}
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          {imageUrls.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {imageUrls.map((url, i) => (
                <div key={url} className="relative group/img">
                  <img src={url} alt="" className="w-20 h-20 object-cover rounded-xl border border-glass-border" />
                  <button
                    onClick={() => setImageUrls((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-glass-border flex items-center justify-center text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  {operation === 'first_last_frame' && (
                    <span className="absolute bottom-1 left-1 px-1 rounded bg-black/70 text-[9px] text-white">
                      {i === 0 ? t('home.first') : t('home.last')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {imageUrls.length < imgReq.max && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-glass-border text-text-tertiary hover:border-primary/40 hover:text-text-secondary transition-colors disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" />}
              <span className="text-sm">{uploading ? t('home.uploading') : t('home.uploadImage')}</span>
            </button>
          )}
        </div>
      )}

      {/* 4. Settings */}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-3">{t('home.aspectRatio')}</label>
          <AspectRatioSelector
            options={model.aspectRatios}
            value={effAspect}
            onChange={setAspectRatio}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-3">{t('home.duration')}</label>
          <PillSelector
            options={model.durations}
            value={effDuration}
            onChange={setDuration}
          />
        </div>
        {model.resolutions.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-3">{t('home.quality')}</label>
            <PillSelector
              options={model.resolutions}
              value={effResolution}
              onChange={setResolution}
            />
          </div>
        )}
      </div>

      {/* 5. Advanced (collapsible) */}
      {hasAdvanced && (
        <div className="bg-surface-light rounded-2xl border border-glass-border overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4"
          >
            <span className="text-sm font-semibold text-text-primary">{t('home.advancedSettings')}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-text-secondary transition-transform',
                showAdvanced && 'rotate-180'
              )}
            />
          </button>
          {showAdvanced && (
            <div className="px-5 pb-5 space-y-5">
              {model.supportsAudio && (
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-text-secondary">{t('home.generateAudio')}</label>
                  <Toggle checked={generateAudio} onChange={setGenerateAudio} label={t('home.generateAudio')} />
                </div>
              )}
              {model.supportsAutoFix && (
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-text-secondary">{t('home.autoFixPrompt')}</label>
                  <Toggle checked={autoFix} onChange={setAutoFix} label={t('home.autoFixPrompt')} />
                </div>
              )}
              {model.supportsNegativePrompt && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('home.negativePrompt')}
                  </label>
                  <textarea
                    rows={2}
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder={t('home.negativePromptPlaceholder')}
                    className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary resize-none"
                  />
                </div>
              )}
              {model.supportsSeed && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.seed')}</label>
                  <input
                    type="number"
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    placeholder={t('home.seedPlaceholder')}
                    className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
              )}
              {/* Style preset (PixVerse / Vidu) — fal `style` enum. */}
              {model.styleOptions && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.style')}</label>
                  <div className="flex flex-wrap gap-2">
                    {model.styleOptions.map((o) => (
                      <button
                        key={o.value || 'default'}
                        type="button"
                        onClick={() => setStyle(o.value)}
                        className={cn(
                          'px-4 py-2 rounded-full border text-sm font-medium transition-all',
                          style === o.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/30'
                        )}
                      >
                        {t(`models.style.${o.value || 'default'}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Motion intensity (Vidu) — fal `movement_amplitude`. */}
              {model.supportsMotion && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.motionIntensity')}</label>
                  <div className="flex flex-wrap gap-2">
                    {MOTION_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setMotion(o.value)}
                        className={cn(
                          'px-4 py-2 rounded-full border text-sm font-medium transition-all',
                          motion === o.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/30'
                        )}
                      >
                        {t(o.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Prompt adherence (Kling) — friendly mapping over fal `cfg_scale`. */}
              {model.supportsCfgScale && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.promptAdherence')}</label>
                  <div className="flex flex-wrap gap-2">
                    {(['Loose', 'Balanced', 'Strict'] as const).map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setCfgPreset(o)}
                        className={cn(
                          'px-4 py-2 rounded-full border text-sm font-medium transition-all',
                          cfgPreset === o
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/30'
                        )}
                      >
                        {t(`home.cfg${o}`)}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-text-tertiary">{t('home.cfgHelp')}</p>
                </div>
              )}

              {model.supportsShotType && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.shotType')}</label>
                  <div className="flex flex-wrap gap-2">
                    {(['customize', 'intelligent'] as const).map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setShotType(o)}
                        className={cn(
                          'px-4 py-2 rounded-full border text-sm font-medium transition-all',
                          shotType === o
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/30'
                        )}
                      >
                        {t(o === 'customize' ? 'home.shotSingle' : 'home.shotMulti')}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-text-tertiary">{t('home.shotTypeHelp')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <PromptBlockedNotice reason={blocked} onClose={() => setBlocked(null)} />
      {error && <p className="text-xs text-amber-300/80">{error}</p>}

      {/* Generate */}
      <button
        onClick={() => void handleCreate()}
        disabled={busy || !isValid || uploading}
        className="w-full relative py-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-base font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
        {busy ? t('home.creating') : t('home.createVideo')}
        {/* Credit cost shown to PRO only — free users see no price (generation reads as free). */}
        {isPro() && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-white/15 rounded">
            {t('home.creditsLabel', { count: creditCost })}
          </span>
        )}
      </button>

      {/* Free-tier Manual funnel now lives on the shared project-page route (/auto-preview);
          handleCreate navigates there with the manual intent → loader → ready gate → paywall. */}
    </div>
  );
}

/**
 * Hero title with the "Vivideo" brand word styled (gradient). The full phrase is
 * translated as one unit (e.g. "Say it with Vivideo" → tr "Vivideo ile söyleyin")
 * so word order is correct per language; "Vivideo" stays literal in every locale,
 * so we split on it to colour just the brand. Falls back to plain text if absent.
 */
function HeroTitle({ text }: { text: string }) {
  const i = text.indexOf('Vivideo');
  if (i === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="gradient-text">Vivideo</span>
      {text.slice(i + 'Vivideo'.length)}
    </>
  );
}

export function HomePage() {
  const { t } = useTranslation('v2');
  const [mode, setMode] = useState<HomeMode>('agent');
  const isPro = useUserStore((s) => s.isPro);
  const userPlan = useUserStore((s) => s.user?.userPlan);
  // Returning from checkout with a stashed manual generation → open Manual mode
  // so ManualMode mounts and its auto-resume effect re-runs it (see MANUAL_STASH_KEY).
  useEffect(() => {
    try {
      if (localStorage.getItem(MANUAL_STASH_KEY) && isPro()) setMode('manual');
    } catch { /* ignore */ }
  }, [userPlan, isPro]);

  // Templates v2 "Use this" seeds a MANUAL preset (model/prompt/duration) →
  // open Manual mode so ManualMode mounts and applies it (consumeHomeManualSeed).
  useEffect(() => {
    if (peekHomeManualSeed()) setMode('manual');
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute -top-32 left-1/4 w-[600px] h-[400px] bg-primary/15 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute -top-16 right-0 w-[400px] h-[300px] bg-accent/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-12">
        {/* Hero */}
        <div className="text-center mb-6 sm:mb-8">
          {/* Deliberately modest: the composer below is the conversion surface,
              the headline just labels it. */}
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2 animate-fade-in"><HeroTitle text={t('home.heroTitle')} /></h1>
          <p className="text-sm sm:text-base text-text-secondary animate-fade-in" style={{ animationDelay: '120ms' }}>{t('home.heroSubtitle')}</p>
        </div>

        {/* Auto / Manual mode switcher */}
        <ModeSwitcher mode={mode} onChange={setMode} />

        {mode === 'agent' ? <AgentMode /> : <ManualMode />}
      </div>
    </div>
  );
}
